import { useEffect, useState } from "react";
import CustomModal, { BtnState, BtnType } from "..";
import {
  SpendBudget,
  SpendSeason,
  SpendTransactionFix,
  useSpendGroupByIdLazyQuery,
  useSpendUpdateBudgetTransactionBudgetItemMutation,
} from "graphql/generated";
import { TRANSACTION_FIX_KEY } from "../../../constants";
import Step1Details from "./step-1-details";
import Step2GroupSelect from "./step-2-group-select";
import { groupBy } from "helpers/list";
import { useAppContexts } from "hooks/use-context";
import Step3TransactionSelect from "./step-3-transaction-select";
import Step4Fixes from "./step-4-fixes";

type TransactionFixesProps = {
  isOpen: boolean;
  toggle: () => void;
  transactionFixes: SpendTransactionFix[];
  setTransactionsDismissed: React.Dispatch<React.SetStateAction<boolean>>;
};

function TransactionFixes({
  isOpen,
  toggle,
  transactionFixes,
  setTransactionsDismissed,
}: TransactionFixesProps) {
  const { toast } = useAppContexts();
  const [seasons, setSeasons] = useState<SpendSeason[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<{
    id: string;
    name: string;
  }>();
  const [selectedGroup, setSelectedGroup] = useState<{
    id: string;
    name: string;
  }>();
  const [step, setStep] = useState(0);
  const [groupsAffected, setGroupsAffected] = useState<{
    keys: string[];
    result: Record<string, SpendTransactionFix[]>;
  }>();
  const [fixTransactions, setFixTransactions] = useState<SpendTransactionFix[]>(
    []
  );
  const [selectedTransaction, setSelectedTransaction] =
    useState<SpendTransactionFix>();
  const [selectedBudgetItem, setSelectedBudgetItem] = useState<SpendBudget>();

  const [getGroupById, { loading: loadingGroup, data: groupData }] =
    useSpendGroupByIdLazyQuery();

  const [
    updateBudgetTransaction,
    {
      loading: loadingBudgetTransactionUpdate,
      data: updateBudgetTransactionData,
    },
  ] = useSpendUpdateBudgetTransactionBudgetItemMutation();

  useEffect(() => {
    if (!loadingGroup && groupData) {
      if (!groupsAffected?.result[selectedGroup?.id ?? ""]?.length) {
        setSelectedGroup(undefined);
        handleClear();
        setStep(1);
      } else {
        setSeasons(groupData.spendGroupById?.seasons as SpendSeason[]);
        setFixTransactions(groupsAffected?.result[selectedGroup?.id!] ?? []);
        if (step < 2) {
          setStep(2);
        }
      }
    }
    // eslint-disable-next-line
  }, [loadingGroup, groupData, groupsAffected?.result]);

  useEffect(() => {
    setGroupsAffected(groupBy(transactionFixes, (e) => e.groupId));
  }, [transactionFixes]);

  useEffect(() => {
    if (
      !loadingBudgetTransactionUpdate &&
      updateBudgetTransactionData?.spendUpdateBudgetTransactionBudgetItem
    ) {
      toast?.setToast({
        message: "Transaction budget item updated",
        type: "success",
      });
      handleClear();
      setStep(2);
    }
    // eslint-disable-next-line
  }, [loadingBudgetTransactionUpdate, updateBudgetTransactionData]);

  const handleCancel = () => {
    localStorage.setItem(TRANSACTION_FIX_KEY, JSON.stringify(true));
    setTransactionsDismissed(true);
    toggle();
  };
  const handleClear = () => {
    setSelectedTransaction(undefined);
    setSelectedSeason(undefined);
    setSelectedBudgetItem(undefined);
  };

  const handleSubmitUpdate = () => {
    if (!selectedTransaction?.id) {
      toast?.setToast({
        message: "Please select a transaction",
        type: "danger",
      });
    } else if (!selectedBudgetItem?.id) {
      toast?.setToast({
        message: "Please select a budget item",
        type: "danger",
      });
    } else {
      updateBudgetTransaction({
        variables: {
          input: {
            transactionFixId: selectedTransaction.id,
            budgetTransactionId: selectedTransaction.budgetTrxId!,
            newBudgetItemId: selectedBudgetItem.id,
          },
        },
        refetchQueries: ["SpendTransactionFixes"],
      });
    }
  };

  const modalOptions: {
    btn1: BtnType;
    btn2: BtnType;
    component: JSX.Element;
  }[] = [
    {
      btn1: {
        text: "Fix Now",
        onClick: () => {
          setStep(1);
        },
      },
      btn2: {
        text: "Remind Me Later",
        onClick: handleCancel,
      },
      component: (
        <Step1Details
          numOfTransactions={transactionFixes.length}
          numOfGroups={groupsAffected?.keys.length ?? 0}
        />
      ),
    },
    {
      btn1: {
        text: "Next",
        onClick: () => {
          if (!selectedGroup) {
            toast?.setToast({
              message: "Please select a group to continue",
              type: "danger",
            });
          } else {
            getGroupById({
              variables: {
                spendGroupByIdId: selectedGroup.id,
              },
              fetchPolicy: "network-only",
            });
          }
        },
        btnState: loadingGroup ? BtnState.DISABLED : BtnState.BASE,
      },
      btn2: {
        text: "Back",
        onClick: () => {
          setSelectedGroup(undefined);
          setStep(0);
        },
      },
      component: (
        <Step2GroupSelect
          groupsAffected={groupsAffected}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
        />
      ),
    },
    {
      btn1: {
        text: "Next",
        onClick: () => {
          if (!selectedTransaction) {
            toast?.setToast({
              message: "Please select a transaction to continue",
              type: "danger",
            });
          } else {
            setStep(3);
          }
        },
      },
      btn2: {
        text: "Back",
        onClick: () => {
          setSelectedTransaction(undefined);
          setStep(1);
        },
      },
      component: (
        <Step3TransactionSelect
          fixTransactions={fixTransactions}
          selectedTransaction={selectedTransaction}
          setSelectedTransaction={setSelectedTransaction}
        />
      ),
    },
    {
      btn1: {
        text: "Submit",
        onClick: handleSubmitUpdate,
      },
      btn2: {
        text: "Back",
        onClick: () => {
          setSelectedSeason(undefined);
          setSelectedBudgetItem(undefined);
          setStep(2);
        },
      },
      component: (
        <Step4Fixes
          seasons={seasons}
          selectedTransaction={selectedTransaction}
          selectedSeason={selectedSeason}
          setSelectedSeason={setSelectedSeason}
          selectedBudgetItem={selectedBudgetItem}
          setSelectedBudgetItem={setSelectedBudgetItem}
        />
      ),
    },
  ];

  return (
    <CustomModal
      title="Transaction Reconciliation Needed"
      isOpen={isOpen}
      toggle={handleCancel}
      btn1={modalOptions[step].btn1}
      btn2={modalOptions[step].btn2}
      customOverflow="visible"
    >
      <div className="modal-card">{modalOptions[step].component}</div>
    </CustomModal>
  );
}

export default TransactionFixes;
